import store from '@Store';

import * as BatchFileFactory from '@Services/BatchFileFactory';
import * as QueryFileFactory from '@Services/QueryFileFactory';
import * as SearchImportFile from '@Services/SearchImportFile';

import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';

import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import MessageBroker from '@Services/MessageBroker';
import first from 'lodash/first';
import RequestUrls from '@Constants/RequestUrlsConstants';
import identity from 'lodash/identity';
import { openProjectParametersDialog } from '@Pages/RetroSynthesis/ProjectParametersDialog/action';
import { noCache } from '@Utils/Network';
import { uploadedFile as uploadedFileSelector, isClosed as isClosedSelector } from './ImportFileModalSelectors';
import {
  typeOfImportFileModal,
  importFileModalSetIsLoadingReducerType,
  importFileModalSetUploadedFileReducerType,
  importFileModalSetIsClosedReducerType,
} from './ImportFileModalConstants';
import { initialState } from './ImportFileModalReducer';

export const closeImportFileModal = () => (dispatch) => {
  dispatch(
    closeDialog({
      id: typeOfImportFileModal,
    })
  );
};

export const dispatchOpenImportFileModal = (...args) => {
  store.dispatch(
    updateDialog({
      id: typeOfImportFileModal,
      dialogType: typeOfImportFileModal,
      movable: false,
      backdrop: false,
      closeButton: false,
      isRt: args[0],
    })
  );
};

export function setIsLoading(isLoading) {
  return {
    type: importFileModalSetIsLoadingReducerType,
    payload: { isLoading },
  };
}

export function setUploadedFile(uploadedFile) {
  return {
    type: importFileModalSetUploadedFileReducerType,
    payload: { uploadedFile },
  };
}

export function setIsClosed(isClosed) {
  return {
    type: importFileModalSetIsClosedReducerType,
    payload: { isClosed },
  };
}

export const resetState = () => (dispatch) => {
  dispatch(setIsLoading(initialState.get('isLoading')));
  dispatch(setIsClosed(initialState.get('isClosed')));
  dispatch(setUploadedFile(initialState.get('uploadedFile')));
};

let selfDestroyLocationChange;

export const closeModal = () => (dispatch) => {
  dispatch(setIsClosed(true));
  dispatch(closeImportFileModal());

  if (selfDestroyLocationChange) {
    selfDestroyLocationChange();
  }
};

export const initialize = (history) => (dispatch) => {
  dispatch(resetState());
  if (history) {
    selfDestroyLocationChange = history.listen(() => {
      dispatch(closeModal());
    });
  }
};

export const isBatchSearchNeeded = (error) => {
  switch (
    error &&
    error.errorNumber // case 1 (show error message from BE)
  ) {
    case 18:
      return false;
    case 19: // case 3 (show no message if we send batch search request after query import)
      return true;
    case 5: // in case it's a default error
      return false;
    default:
      return true;
  }
};

export const importQuery = () => (dispatch, getState) => {
  const importFromFileSuccess = () => {
    dispatch(closeModal());
  };

  const importFromFileFailed = (error) => {
    dispatch(closeModal());
    SearchImportFile.showErrorAlert(error);
  };

  dispatch(setIsLoading(true));

  const uploadedFile = uploadedFileSelector(getState());
  return QueryFileFactory.parseFile(uploadedFile) // Try to Parse as Query File
    .then((queryFile) => {
      if (!isClosedSelector(getState())) {
        QueryFileFactory.showQueryFile(queryFile);
      }
    })
    .then(importFromFileSuccess)

    .catch((error) => {
      if (!isBatchSearchNeeded(error)) {
        return importFromFileFailed(error);
      }

      if (isClosedSelector(getState())) {
        // eslint-disable-next-line consistent-return
        return;
      }

      return BatchFileFactory.parseFile(uploadedFile) // Try parse as Batch file
        .then((batchFile) => {
          if (isClosedSelector(getState())) {
            return;
          }

          // if only one query is contained then load it into query builder and show query builder
          if (batchFile.totalQueriesCount === 1 && batchFile.validQueriesCount === 1) {
            // eslint-disable-next-line consistent-return
            return QueryFileFactory.showQueryFile(first(batchFile.queries));
          }
          // eslint-disable-next-line consistent-return
          return BatchFileFactory.showBatchFile(batchFile);
        })
        .then(importFromFileSuccess, importFromFileFailed)
        .catch(Logger.error);
    })
    .finally(() => {
      dispatch(setIsLoading(false));
    });
};

export const importRT = () => (dispatch, getState) => {
  const uploadedFile = uploadedFileSelector(getState());
  const importRTValidateSuccess = () => {
    dispatch(closeModal());
    dispatch(openProjectParametersDialog({ file: uploadedFile }));
  };
  const importRTValidateFailed = (error) => {
    dispatch(closeModal());
    // show error
    NotifyDialogsService.openModalWindow({
      type: 'error',
      title: 'Error',
      text: error.message,
      confirmText: 'Ok',
    });
  };

  dispatch(setIsLoading(true));
  const requestData = new FormData();
  requestData.append('file', uploadedFile);
  return MessageBroker.send(RequestUrls.rsProjectsImportValidate, requestData, {
    ...noCache(),
    transformRequest: identity,
    headers: { 'Content-Type': undefined },
  })
    .then(() => {
      importRTValidateSuccess();
    })
    .catch((error) => {
      importRTValidateFailed(error);
    })
    .finally(() => {
      dispatch(setIsLoading(false));
    });
};
